/**
 * 購入サブシステム 公開APIクライアント (mfk-purchase-subsystem P2)
 * 未認証(公開)ルート /purchase/* を呼ぶ。認証トークンは付けない。
 */
import { getRecaptchaToken } from './recaptcha';

const API_BASE = import.meta.env.VITE_API_URL || '';

export interface Product {
  code: string;
  name: string;
  unitPrice: number;
  unit: 'per_user_year' | 'per_user_month';
  paymentTerms: 'A' | 'A_or_B';
  taxRate: number | string;
  active: boolean;
}

export type PaymentMethod = 'lump' | 'monthly12';

export interface OrderItem {
  id: string;
  productCode: string;
  productName: string;
  userCount: number;
  paymentMethod: PaymentMethod;
  unitPrice: number;
  amountExcluded: number;
  amountIncluded: number;
  subscriptionId: string | null;
}

export interface BillingCompany {
  id: string;
  name: string;
  zip: string;
  address: string;
  tel: string;
  presidentName: string;
  email: string;
  settlementEmail?: string | null;
  paymentMethodChoice?: 'bank_transfer' | 'account_transfer';
  paymentMethodStatus?: string;
  companyId: string | null;
  createdAt: string;
}

export interface Order {
  id: string;
  status: string;
  approvedAt: string | null;
  contractAgreed: boolean;
  privacyAgreed: boolean;
  dataAnalysisOptIn: boolean;
  totalAmountExcluded: number;
  totalAmountIncluded: number;
  createdAt: string;
  billingCompany: BillingCompany | null;
  items: OrderItem[];
}

/** 申込確認画面で取得する同意情報 */
export interface OrderConsent {
  contractAgreed: boolean;
  privacyAgreed: boolean;
  dataAnalysisOptIn: boolean;
}

async function req<T>(path: string, method: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((json as { message?: string; error?: string }).message || (json as { error?: string }).error || `HTTP ${res.status}`);
  }
  return json as T;
}

export const purchaseApi = {
  getProducts: () => req<{ products: Product[] }>(`/purchase/products`, 'GET'),
  /** 郵便番号(7桁)→住所。バックエンド経由(zipcloudプロキシ)。失敗時 address=null。 */
  lookupZipAddress: (zip7: string) =>
    req<{ address: string | null }>(`/purchase/zip-address?zip=${encodeURIComponent(zip7)}`, 'GET'),
  /** メール存在チェック（決済に進む前・初回のみ）。exists=true ならログイン誘導する。 */
  checkEmail: async (email: string) => {
    const recaptchaToken = await getRecaptchaToken('purchase_check_email');
    return req<{ exists: boolean }>(`/purchase/check-email`, 'POST', { email, recaptchaToken });
  },
  createCompany: async (input: {
    name: string;
    zip: string;
    address: string;
    tel: string;
    presidentName: string;
    email: string;
    settlementEmail?: string;
  }) => {
    const recaptchaToken = await getRecaptchaToken('purchase_company');
    // 未登録メール時にバックエンドが Cognito アカウント（招待メール=仮PW）＋会社＋社長ユーザーを作成する。
    return req<{ billingCompany: BillingCompany; accountCreated: boolean }>(
      `/purchase/companies`,
      'POST',
      { ...input, recaptchaToken }
    );
  },
  createOrder: (input: {
    billingCompanyId: string;
    items: { productCode: string; userCount: number; paymentMethod: PaymentMethod }[];
    consent: OrderConsent;
  }) => req<{ order: Order }>(`/purchase/orders`, 'POST', input),
  approveOrder: (orderId: string) => req<{ order: Order }>(`/purchase/orders/${orderId}/approve`, 'POST'),
  getOrder: (orderId: string) => req<{ order: Order }>(`/purchase/orders/${orderId}`, 'GET'),
  checkout: (orderId: string, paymentMethod?: 'bank_transfer' | 'account_transfer') =>
    req<{ order: Order; note: string }>(
      `/purchase/orders/${orderId}/checkout`,
      'POST',
      paymentMethod ? { paymentMethod } : undefined
    ),
};
